import { useFunctionalInterfacesStore } from "@/frontend/store/FunctionalInterfaceStore";
import { useImplementationStore } from "@/frontend/store/ImplementationStore";
import { useProcedureInstanceStore } from "@/frontend/store/ProcedureInstanceStore";
import { Lightning } from "@/frontend/SVGs/Lightning";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { BasicButton } from "../buttons/BasicButton";
import { ProgressBar } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ProcedurePlaceholder } from "../containers/ContainersPlaceholder";
import { FunctionalInterfaceVersion } from "@/backend/models/functionalInterfaceVersion";
import { I18n } from "@/i18n/I18n";
import { useProcedureListStore } from "@/frontend/store/ProcedureListStore";
import { NotFound } from "@/frontend/pages/NotFound";

export function ProcedureImplementationSetupWizardLoader() {
  const { procedureId } = useParams();
  const fetchProcedure = useProcedureInstanceStore((state) => state.fetchProcedureInstance);
  const resetProcedure = useProcedureInstanceStore((state) => state.reset);
  const fetchAllLatestFunctionalInterfaceVersions = useFunctionalInterfacesStore((state) => state.fetchAllLatestVersions);
  const readVersionsForProcedure = useFunctionalInterfacesStore((state) => state.readVersionsForProcedure);
  const resetFunctionalInterfaces = useFunctionalInterfacesStore((state) => state.reset);
  const createProcedureFromTemplate = useProcedureListStore((state) => state.createProcedureFromTemplate);
  const navigate = useNavigate();

  const procedure = useProcedureInstanceStore((state) => state.procedureInstance);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (procedureId) {
      const fetch = async () => {
        setLoading(true);
        await Promise.all([fetchProcedure(procedureId), fetchAllLatestFunctionalInterfaceVersions()]);
        setLoading(false);
      };

      void fetch();
    }
    return () => {
      resetFunctionalInterfaces();
      resetProcedure();
    };
  }, [procedureId]);

  useEffect(() => {
    if (procedure?.procedureTemplateId) {
      void readVersionsForProcedure(procedure.procedureTemplateId);
    }
  }, [procedure?.procedureTemplateId]);

  if(loading) return <ProcedurePlaceholder />;
  if(!procedure) return <NotFound />;
  
  const generateFunction = async (assignments: { [key: string]: string }) => {
    void createProcedureFromTemplate(procedure, assignments).then((response) => {
      if (response?.status === 200) {
        void navigate(`/procedures/wizard/${procedure.id}/setup`);
      }
    });
  };

  return <ProcedureImplementationSetupWizard generateFunction={generateFunction} />;
}

export function ProcedureImplementationSetupWizard(props: { generateFunction: (assignments: { [key: string]: string }) => Promise<void> }) {
  const { generateFunction } = props;

  const procedure = useProcedureInstanceStore((state) => state.procedureInstance);
  const versionsInProcedure = useFunctionalInterfacesStore(useShallow(state => state.functionalInterfaceVersions));

  const [functionalInterfaceImplementationAssignments, setFunctionalInterfaceImplementationAssignments] = useState<{ [key: string]: string }>({});
  const [currentPage, setCurrentPage] = useState(0);
  const percentComplete = currentPage / versionsInProcedure.length * 100;


  if (versionsInProcedure.length === 0) return <NotFound />;

  const afterSelect = (functionalInterfaceId: string, implementationId: string) => {
    const assignments = { ...functionalInterfaceImplementationAssignments, [functionalInterfaceId]: implementationId };
    if(Object.keys(assignments).length === versionsInProcedure.length) {
      void generateFunction(assignments);
    } else {
      setFunctionalInterfaceImplementationAssignments(assignments);
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className='h-100 overflow-auto' style={{ maxHeight: '100vh', height: '100vh' }}>
      <div className='p-3'>
        <div className="d-flex justify-content-between">
          <strong>{I18n.get('procedureSetup')}</strong>
          <small>{I18n.get('interfacesConfigured', { current: currentPage.toString(), total: versionsInProcedure.length.toString() })}</small>
        </div>
        <small>{procedure?.title}</small>
        <div className='mt-2'><ProgressBar now={percentComplete} /></div>
      </div>
      <ImplementationSelectForInterface version={versionsInProcedure[currentPage]} afterSelect={afterSelect} />
    </div>
  );
}

function ImplementationSelectForInterface(props: { version: FunctionalInterfaceVersion, afterSelect: (functionalInterfaceId: string, implementationId: string) => void }) {
  const { version, afterSelect } = props;
  const functionalInterface = useFunctionalInterfacesStore(state => state.functionalInterfaces.find(functionalInterface => functionalInterface.id === version.functionalInterfaceId));
  const implementations = useImplementationStore(state => state.implementations);
  const fetchAllMatchingFunctionalInterface = useImplementationStore(state => state.fetchAllMatchingFunctionalInterface);
  const resetImplementations = useImplementationStore(state => state.reset);

  if (!functionalInterface) { return null; }

  useEffect(() => {
    void fetchAllMatchingFunctionalInterface(version.id);
    return () => {
      resetImplementations();
    };
  }, [version.id]);

  return (
    <div className='h-100 card card-header rounded-0'>
    <div className='card bg-primary-subtle border-primary p-3'>
      <div className='d-flex align-items-center gap-2 text-primary'>
        <Lightning /><strong>{functionalInterface.name}</strong>
      </div>
    </div>
    <div className='card p-3 mt-3'>
      <strong>{I18n.get('availableImplementations', { count: implementations.length.toString() })}</strong>
      {implementations.map((implementation) => (
        <div className='card p-3' key={implementation.id}>
          <div className='d-flex justify-content-between'>
            <strong>{implementation.title}</strong>
            <BasicButton label={I18n.get('select')} className='btn btn-sm btn-primary' onClick={() => afterSelect(version.id, implementation.id)} />
          </div>
        </div>
      ))}
    </div>
  </div>
  );
}
